/**
 * Backoffice CRM: User App Feedback Controller
 *
 * Two kinds of feedback share the page (#2173): the apps' feedback (`app_feedback`) and recipe
 * ratings, the anonymous documents the Rate Recipe sheet writes to `recipes/{id}/feedback`
 * (score, quick tags, chef's note, app version, device; never a user or family id). The kind tabs
 * switch the list; the Open and Handled tabs, the search, the drawer and the handled and delete
 * actions work on whichever kind is shown.
 */

import { setNavBadge } from "./ui/shell.js";
import { BADGE, BUTTON, checkbox } from "./ui/classes.js";
import { solidIcon } from "./ui/icons.js";
import { showDialog, hideDialog } from "./ui/dialog.js";
import { foodScoreReaction, scoreHtml } from "./recipeRatings.js";

let feedbackList = [];
// The recipe ratings, newest first, at most 100 (fetchRecipeRatings). Each carries `recipe_id`, read
// from the document's path, beside the document's own fields.
let ratingsList = [];
let firestoreCtx = null;
let activeSearchTerm = "";
let activeTopicFilter = "ALL";
// "app" or "ratings": which kind the table shows (#2173).
let activeKind = "app";
// The ratings tab's "With a note only" toggle.
let ratingsWithNoteOnly = false;
// "open" or "handled": the tab the table shows (#1421). A feedback is handled when its document
// says `handled: true`; the apps never write the field, so a new feedback is open.
let activeStatusTab = "open";
let selectedFeedbackItem = null;
const selectedFeedbackIds = new Set();
// What the app offered after each feedback (#2647), by feedback id: `help_suggestion_outcomes`, which the app writes
// once when the customer has seen the articles, opened one or not, and said whether it helped.
let helpOutcomes = new Map();

/**
 * Loads the feedback.html partial template into the container
 */
export async function loadFeedbackTemplate() {
  const container = document.getElementById("sectionFeedbackContainer");
  if (!container) return;

  try {
    const res = await fetch("templates/feedback.html");
    if (!res.ok) throw new Error(`HTTP error ${res.status}`);
    container.innerHTML = await res.text();
  } catch (err) {
    console.error("❌ Failed to load feedback.html template:", err);
    container.innerHTML = `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Could not load the feedback template: ${escapeHtml(err.message)}</div>`;
  }
}

/**
 * Initializes the feedback controller with Firestore bindings. It reads nothing: the template is
 * mounted before anyone has signed in, so app.js calls fetchFeedbackList once an admin is in (#1333).
 */
export function initFeedbackController(ctx) {
  firestoreCtx = ctx;

  const searchInput = document.getElementById("feedbackSearchInput");
  const topicFilter = document.getElementById("feedbackTopicFilter");
  const refreshBtn = document.getElementById("refreshFeedbackBtn");
  const copyNoteBtn = document.getElementById("modalCopyNoteBtn");
  const modal = document.getElementById("feedbackDetailModal");
  const deleteSelectedBtn = document.getElementById("deleteSelectedFeedbackBtn");
  const modalDeleteBtn = document.getElementById("modalDeleteFeedbackBtn");
  const modalHandledBtn = document.getElementById("modalToggleHandledBtn");
  const handledSelectedBtn = document.getElementById("toggleHandledSelectedFeedbackBtn");
  const selectAll = document.getElementById("feedbackSelectAll");
  const tabOpen = document.getElementById("feedbackTabOpen");
  const tabHandled = document.getElementById("feedbackTabHandled");
  const kindApp = document.getElementById("feedbackKindApp");
  const kindRatings = document.getElementById("feedbackKindRatings");
  const withNoteOnly = document.getElementById("feedbackWithNoteOnly");

  if (searchInput) {
    searchInput.addEventListener("input", (e) => {
      activeSearchTerm = e.target.value.trim().toLowerCase();
      renderFeedbackTable();
    });
  }

  if (topicFilter) {
    topicFilter.addEventListener("change", (e) => {
      activeTopicFilter = e.target.value;
      renderFeedbackTable();
    });
  }

  if (refreshBtn) {
    refreshBtn.addEventListener("click", async () => {
      await (activeKind === "ratings" ? fetchRecipeRatings() : fetchFeedbackList());
    });
  }

  if (kindApp) kindApp.addEventListener("click", () => switchFeedbackKind("app"));
  if (kindRatings) kindRatings.addEventListener("click", () => switchFeedbackKind("ratings"));
  if (withNoteOnly) {
    withNoteOnly.addEventListener("change", (e) => setRatingsWithNoteOnly(Boolean(e.target.checked)));
  }

  // The drawer's close buttons, Esc and the backdrop all close the <dialog> (el-dialog).
  if (modal) modal.addEventListener("close", () => { selectedFeedbackItem = null; });

  if (copyNoteBtn) {
    copyNoteBtn.addEventListener("click", () => {
      if (!selectedFeedbackItem || !noteOf(selectedFeedbackItem)) return;
      navigator.clipboard.writeText(noteOf(selectedFeedbackItem));
      if (firestoreCtx && firestoreCtx.showToast) {
        firestoreCtx.showToast("Feedback note copied to clipboard!", "success");
      }
    });
  }

  if (deleteSelectedBtn) {
    deleteSelectedBtn.addEventListener("click", () => deleteFeedback(Array.from(selectedFeedbackIds)));
  }

  if (modalDeleteBtn) {
    modalDeleteBtn.addEventListener("click", async () => {
      if (!selectedFeedbackItem) return;
      if (await deleteFeedback([selectedFeedbackItem.id])) closeFeedbackModal();
    });
  }

  if (modalHandledBtn) {
    modalHandledBtn.addEventListener("click", async () => {
      if (!selectedFeedbackItem) return;
      if (await setFeedbackHandled([selectedFeedbackItem.id], !isHandled(selectedFeedbackItem))) closeFeedbackModal();
    });
  }

  if (handledSelectedBtn) {
    handledSelectedBtn.addEventListener("click", () => setFeedbackHandled(Array.from(selectedFeedbackIds), activeStatusTab === "open"));
  }

  if (tabOpen) tabOpen.addEventListener("click", () => switchFeedbackTab("open"));
  if (tabHandled) tabHandled.addEventListener("click", () => switchFeedbackTab("handled"));

  if (selectAll) {
    selectAll.addEventListener("change", (e) => {
      const visibleIds = filterFeedback().map((f) => f.id);
      visibleIds.forEach((id) => (e.target.checked ? selectedFeedbackIds.add(id) : selectedFeedbackIds.delete(id)));
      renderFeedbackTable();
    });
  }
}

/**
 * Fetches recent app feedback records from Firestore
 */
export async function fetchFeedbackList() {
  if (!firestoreCtx) return;
  const { db, collection, getDocs, query, orderBy, limit, showToast } = firestoreCtx;

  try {
    let snap;
    try {
      if (typeof orderBy === "function" && typeof limit === "function") {
        const q = query(collection(db, "app_feedback"), orderBy("created_at", "desc"), limit(100));
        snap = await getDocs(q);
      } else {
        snap = await getDocs(collection(db, "app_feedback"));
      }
    } catch (queryErr) {
      console.warn("⚠️ Fallback to unindexed collection query:", queryErr.message);
      snap = await getDocs(collection(db, "app_feedback"));
    }
    
    feedbackList = snap.docs.map((d) => ({
      id: d.id,
      ...d.data()
    }));
    helpOutcomes = await fetchHelpOutcomes();
    if (activeKind === "app") pruneSelection(feedbackList);
    sortNewestFirst(feedbackList);

    renderFeedbackTable();
  } catch (err) {
    console.error("❌ Failed to fetch user feedback:", err);
    showLoadError("app", "Could not load the feedback: " + err.message);
    if (showToast) showToast("Error loading feedback: " + err.message, "error");
  }
}

/**
 * The newest help outcomes, by feedback id. A failure leaves the feedback without them rather than failing the list.
 */
async function fetchHelpOutcomes() {
  const { db, collection, getDocs, query, orderBy, limit } = firestoreCtx;
  try {
    const ref = collection(db, "help_suggestion_outcomes");
    const snap = await getDocs(typeof orderBy === "function" && typeof limit === "function" ? query(ref, orderBy("created_at", "desc"), limit(300)) : ref);
    return new Map(snap.docs.map((d) => [d.id, d.data()]));
  } catch (err) {
    console.warn("⚠️ Help outcomes unreadable:", err.message);
    return new Map();
  }
}

/** The help the app offered after this feedback, or null. */
export function helpOutcomeOf(item) {
  const outcome = item ? helpOutcomes.get(item.id) : null;
  return outcome && typeof outcome === "object" ? outcome : null;
}

/** True when an article the app offered solved it: shown muted, and not counted as needing attention (#2647). */
export function isSolvedByHelp(item) {
  return helpOutcomeOf(item)?.outcome === "solved";
}

/** "family-sharing" → "Family sharing": the CRM has no copy of the help index's titles. */
export function articleName(slug) {
  const words = String(slug || "").replace(/-+/g, " ").trim();
  return words ? words[0].toUpperCase() + words.slice(1) : "";
}

/** "We showed Family sharing; they opened it; it solved it." from an outcome, or "" without one. */
export function helpOutcomeText(outcome) {
  if (!outcome) return "";
  const list = (v) => (Array.isArray(v) ? v.filter((s) => typeof s === "string") : []);
  const shown = list(outcome.shown);
  const opened = list(outcome.opened);
  if (!shown.length) return "No help article fitted.";
  const names = (slugs) => slugs.map(articleName).join(", ");
  const openedPart = !opened.length ? "they opened none"
    : (opened.length === shown.length && opened.every((s) => shown.includes(s)) ? `they opened ${shown.length === 1 ? "it" : "them"}` : `they opened ${names(opened)}`);
  const result = outcome.outcome === "solved" ? "it solved it" : outcome.outcome === "not_solved" ? "it didn't solve it" : "they didn't say whether it helped";
  return `We showed ${names(shown)}; ${openedPart}; ${result}.`;
}

function helpOutcomeHtml(item) {
  const text = helpOutcomeText(helpOutcomeOf(item));
  if (!text) return "";
  const badge = isSolvedByHelp(item) ? `<span class="${BADGE.green}">Solved by help</span> ` : "";
  return `<div class="mt-2 text-xs text-gray-500 dark:text-gray-400" data-help-outcome>${badge}${escapeHtml(text)}</div>`;
}

/**
 * Fetches the newest recipe ratings from every recipe's `feedback` subcollection in one
 * collection-group query (#2173). The rules let an admin read it; `firestore.indexes.json` holds its
 * collection-group index on created_at, so a FAILED_PRECONDITION here means the index is not live.
 */
export async function fetchRecipeRatings() {
  if (!firestoreCtx) return;
  const { db, collectionGroup, getDocs, query, orderBy, limit, showToast } = firestoreCtx;
  if (typeof collectionGroup !== "function") return;

  try {
    const q = query(collectionGroup(db, "feedback"), orderBy("created_at", "desc"), limit(100));
    const snap = await getDocs(q);
    ratingsList = snap.docs.map((d) => ({
      id: d.id,
      recipe_id: d.ref?.parent?.parent?.id || "",
      ...d.data()
    }));
    if (activeKind === "ratings") pruneSelection(ratingsList);
    sortNewestFirst(ratingsList);

    renderFeedbackTable();
  } catch (err) {
    console.error("❌ Failed to fetch recipe ratings:", err);
    showLoadError("ratings", "Could not load the recipe ratings: " + err.message);
    if (showToast) showToast("Error loading recipe ratings: " + err.message, "error");
  }
}

function pruneSelection(list) {
  const loadedIds = new Set(list.map((f) => f.id));
  for (const id of selectedFeedbackIds) {
    if (!loadedIds.has(id)) selectedFeedbackIds.delete(id);
  }
}

function sortNewestFirst(list) {
  list.sort((a, b) => timeOf(b.created_at) - timeOf(a.created_at));
}

function timeOf(ts) {
  if (!ts) return 0;
  if (ts.toMillis) return ts.toMillis();
  const t = new Date(ts).getTime();
  return isNaN(t) ? 0 : t;
}

// A load that failed shows in the table only while its kind is the one on screen.
function showLoadError(kind, message) {
  if (kind !== activeKind) return;
  const tbody = document.getElementById("feedbackTableBody");
  if (tbody) {
    tbody.innerHTML = `<tr><td colspan="6" class="px-6 py-12 text-center text-sm text-red-600 dark:text-red-400">${escapeHtml(message)}</td></tr>`;
  }
}

/** The list the kind tabs have on screen. */
function activeList() {
  return activeKind === "ratings" ? ratingsList : feedbackList;
}

/** True for a recipe rating (a `recipes/{id}/feedback` document), false for app feedback. */
export function isRecipeRating(item) {
  return ratingsList.includes(item);
}

/** The document a list item came from. */
function docRefFor(item) {
  const { db, doc } = firestoreCtx;
  return isRecipeRating(item) ? doc(db, "recipes", item.recipe_id, "feedback", item.id) : doc(db, "app_feedback", item.id);
}

/** The free text of either kind: the app feedback's `note`, the rating's `feedback_note`. */
export function noteOf(item) {
  const text = item?.note ?? item?.feedback_note ?? "";
  return typeof text === "string" ? text.trim() : "";
}

/** The recipe's title from the catalog summary the recipes section loads, or its id. */
function recipeTitleOf(item) {
  const title = typeof firestoreCtx?.recipeTitle === "function" ? firestoreCtx.recipeTitle(item.recipe_id) : null;
  return title || item.recipe_id || "(unknown recipe)";
}

/** Where the CRM shows this recipe (recipesController.viewRecipe sets the same hash). */
export function recipeViewHref(recipeId) {
  return `#recipe-view?id=${encodeURIComponent(recipeId || "")}`;
}

function formatScore(score) {
  if (typeof score !== "number" || isNaN(score)) return "–";
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

function tagLabel(tag) {
  return String(tag).replace(/_/g, " ");
}

/** "1.2.3 · iPhone15,2 · iOS 26.0", from whichever fields the app wrote. */
function ratingDeviceText(item) {
  const os = item.ios_version ? `iOS ${item.ios_version}` : (item.android_version ? `Android ${item.android_version}` : "");
  return [item.app_version ? `v${item.app_version}` : "", item.device_type || "", os].filter(Boolean).join(" · ") || "Unknown device";
}

/**
 * Asks for confirmation, then permanently deletes the given app_feedback documents.
 * @returns {Promise<boolean>} true when the documents were deleted
 */
export async function deleteFeedback(ids) {
  if (!firestoreCtx || !ids || ids.length === 0) return false;
  const { db, doc, writeBatch, showToast } = firestoreCtx;

  const list = activeList();
  const items = ids.map((id) => list.find((f) => f.id === id)).filter(Boolean);
  if (items.length === 0) return false;
  const count = items.length;
  const what = activeKind === "ratings" ? "rating" : "feedback submission";
  const msg = count === 1
    ? describeFeedbackForConfirm(items[0])
    : `Permanently delete ${count} selected ${what}s? This cannot be undone.`;
  if (!window.confirm(msg)) return false;

  try {
    const BATCH_SIZE = 450;
    for (let i = 0; i < items.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      for (const item of items.slice(i, i + BATCH_SIZE)) {
        batch.delete(docRefFor(item));
      }
      await batch.commit();
    }

    const deleted = new Set(items);
    if (activeKind === "ratings") ratingsList = ratingsList.filter((f) => !deleted.has(f));
    else feedbackList = feedbackList.filter((f) => !deleted.has(f));
    ids.forEach((id) => selectedFeedbackIds.delete(id));
    renderFeedbackTable();
    if (showToast) showToast(`Deleted ${count} ${what}${count === 1 ? "" : "s"}.`, "success");
    return true;
  } catch (err) {
    console.error("❌ Failed to delete feedback:", err);
    if (showToast) showToast("Failed to delete feedback: " + err.message, "error");
    return false;
  }
}

/** True when the document says the feedback was handled. */
export function isHandled(item) {
  return item?.handled === true;
}

/** Shows the Open or the Handled tab; the selection is kept only within the tab shown. */
export function switchFeedbackTab(tab) {
  activeStatusTab = tab === "handled" ? "handled" : "open";
  selectedFeedbackIds.clear();
  renderFeedbackTable();
}

/** Shows the app feedback or the recipe ratings (#2173); the selection belongs to one kind. */
export function switchFeedbackKind(kind) {
  activeKind = kind === "ratings" ? "ratings" : "app";
  selectedFeedbackIds.clear();
  renderFeedbackTable();
}

/** The ratings tab's "With a note only" toggle. */
export function setRatingsWithNoteOnly(on) {
  ratingsWithNoteOnly = Boolean(on);
  const box = document.getElementById("feedbackWithNoteOnly");
  if (box) box.checked = ratingsWithNoteOnly;
  renderFeedbackTable();
}

/**
 * Marks the given documents of the kind on screen (app feedback or recipe ratings) handled, or
 * open again, and moves them to the other tab. The document keeps who handled it and when;
 * reopening clears both.
 * @returns {Promise<boolean>} true when the documents were written
 */
export async function setFeedbackHandled(ids, handled) {
  if (!firestoreCtx || !ids || ids.length === 0) return false;
  const { db, writeBatch, serverTimestamp, handledBy, showToast } = firestoreCtx;
  const by = handled && typeof handledBy === "function" ? handledBy() || null : null;
  const list = activeList();
  const items = ids.map((id) => list.find((f) => f.id === id)).filter(Boolean);
  if (items.length === 0) return false;

  try {
    const BATCH_SIZE = 450;
    for (let i = 0; i < items.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      for (const item of items.slice(i, i + BATCH_SIZE)) {
        batch.update(docRefFor(item), {
          handled,
          handled_at: handled ? (typeof serverTimestamp === "function" ? serverTimestamp() : new Date()) : null,
          handled_by: by
        });
      }
      await batch.commit();
    }

    const now = new Date();
    for (const item of items) {
      item.handled = handled;
      item.handled_at = handled ? now : null;
      item.handled_by = by;
    }
    ids.forEach((id) => selectedFeedbackIds.delete(id));
    renderFeedbackTable();
    const count = items.length;
    const what = activeKind === "ratings" ? "rating" : "feedback submission";
    if (showToast) {
      showToast(handled
        ? `Marked ${count} ${what}${count === 1 ? "" : "s"} as handled.`
        : `Reopened ${count} ${what}${count === 1 ? "" : "s"}.`, "success");
    }
    return true;
  } catch (err) {
    console.error("❌ Failed to update feedback:", err);
    if (showToast) showToast("Failed to update feedback: " + err.message, "error");
    return false;
  }
}

/** Where the CRM shows this user's diagnostics (app.js runs the search on arrival, #1421). */
export function userDiagnosticsHref(uid) {
  return `#users?uid=${encodeURIComponent(uid)}`;
}

/** The user id a #users?uid=<uid> address asks for, or null for any other address. */
export function userIdFromHash(hash) {
  const [section, queryString] = String(hash || "").replace("#", "").split("?");
  if (section.toLowerCase() !== "users" || !queryString) return null;
  return new URLSearchParams(queryString).get("uid")?.trim() || null;
}

function userLinkHtml(uid, extraClass = "") {
  if (!uid) return `<span class="font-mono text-xs font-semibold break-all text-gray-900 dark:text-white">anonymous</span>`;
  return `<a href="${escapeHtml(userDiagnosticsHref(uid))}" data-user-link title="Open this user's diagnostics" class="${BUTTON.link} font-mono text-xs break-all ${extraClass}">${escapeHtml(uid)}</a>`;
}

function describeFeedbackForConfirm(item) {
  if (!item) return "Permanently delete this feedback submission? This cannot be undone.";
  const note = noteOf(item);
  const preview = note ? `"${note.length > 80 ? note.slice(0, 80) + "…" : note}"` : "(no note)";
  if (isRecipeRating(item)) {
    return `Permanently delete this rating (${formatScore(item.score)} / 10) of "${recipeTitleOf(item)}"?\n\n${preview}\n\nThis cannot be undone.`;
  }
  return `Permanently delete this feedback from ${item.user_id || "anonymous"}?\n\n${preview}\n\nThis cannot be undone.`;
}

function filterFeedback() {
  if (activeKind === "ratings") return filterRatings();
  return feedbackList.filter((item) => {
    // Open or Handled tab
    if (isHandled(item) !== (activeStatusTab === "handled")) return false;

    // Topic filter
    if (activeTopicFilter !== "ALL") {
      const hasTopic = (item.topics || []).some((t) => t.toLowerCase().includes(activeTopicFilter.toLowerCase()));
      if (!hasTopic) return false;
    }

    // Search filter
    if (activeSearchTerm) {
      const noteMatch = (item.note || "").toLowerCase().includes(activeSearchTerm);
      const userMatch = (item.user_id || "").toLowerCase().includes(activeSearchTerm);
      const familyMatch = (item.family_id || "").toLowerCase().includes(activeSearchTerm);
      const emailMatch = (item.contact_email || "").toLowerCase().includes(activeSearchTerm);
      const topicMatch = (item.topics || []).some((t) => t.toLowerCase().includes(activeSearchTerm));
      if (!noteMatch && !userMatch && !familyMatch && !emailMatch && !topicMatch) return false;
    }

    return true;
  });
}

function filterRatings() {
  return ratingsList.filter((item) => {
    if (isHandled(item) !== (activeStatusTab === "handled")) return false;
    if (ratingsWithNoteOnly && !noteOf(item)) return false;
    if (activeSearchTerm) {
      const haystack = [
        noteOf(item), recipeTitleOf(item), item.recipe_id, ...(item.quick_tags || []).map(tagLabel),
        item.app_version, item.device_type, item.country_code
      ].filter(Boolean).join(" ").toLowerCase();
      if (!haystack.includes(activeSearchTerm)) return false;
    }
    return true;
  });
}

function updateSelectionControls(filtered) {
  const deleteSelectedBtn = document.getElementById("deleteSelectedFeedbackBtn");
  const handledSelectedBtn = document.getElementById("toggleHandledSelectedFeedbackBtn");
  const handledLabelEl = document.getElementById("toggleHandledSelectedLabel");
  const countEl = document.getElementById("selectedFeedbackCount");
  const handledCountEl = document.getElementById("selectedFeedbackHandledCount");
  const selectAll = document.getElementById("feedbackSelectAll");

  const display = selectedFeedbackIds.size > 0 ? "inline-flex" : "none";
  if (deleteSelectedBtn) deleteSelectedBtn.style.display = display;
  if (handledSelectedBtn) handledSelectedBtn.style.display = display;
  if (handledLabelEl) handledLabelEl.textContent = activeStatusTab === "handled" ? "Reopen" : "Mark handled";
  if (countEl) countEl.textContent = selectedFeedbackIds.size;
  if (handledCountEl) handledCountEl.textContent = selectedFeedbackIds.size;
  if (selectAll) {
    const selectedVisible = filtered.filter((f) => selectedFeedbackIds.has(f.id)).length;
    selectAll.checked = filtered.length > 0 && selectedVisible === filtered.length;
    selectAll.indeterminate = selectedVisible > 0 && selectedVisible < filtered.length;
  }
}

/**
 * Renders the feedback table and KPI metrics
 */
export function renderFeedbackTable() {
  const tbody = document.getElementById("feedbackTableBody");
  if (!tbody) return;

  const filtered = filterFeedback();
  const ratings = activeKind === "ratings";
  const list = activeList();
  updateSelectionControls(filtered);
  updateKindControls();

  // Update Stats: the kind on screen
  const statTotal = document.getElementById("statFeedbackTotal");
  const statWithEmail = document.getElementById("statFeedbackWithEmail");
  const statRecent = document.getElementById("statFeedbackRecent");

  const totalCount = list.length;
  const withEmailCount = ratings
    ? ratingsList.filter((f) => noteOf(f)).length
    : feedbackList.filter((f) => f.contact_email && f.contact_email.trim()).length;

  const sevenDaysAgo = Date.now() - (7 * 86400 * 1000);
  const recentCount = list.filter((f) => timeOf(f.created_at) >= sevenDaysAgo).length;

  if (statTotal) statTotal.textContent = totalCount;
  if (statWithEmail) statWithEmail.textContent = withEmailCount;
  if (statRecent) statRecent.textContent = recentCount;

  // The tabs' counts, and the sidebar badge counts what still needs attention: open app feedback
  // and open ratings that came with a note (a bare score needs nobody).
  const handledCount = list.filter(isHandled).length;
  const openCount = totalCount - handledCount;
  const tabOpen = document.getElementById("feedbackTabOpen");
  const tabHandled = document.getElementById("feedbackTabHandled");
  const openCountEl = document.getElementById("feedbackOpenCount");
  const handledCountEl = document.getElementById("feedbackHandledCount");
  if (tabOpen) tabOpen.setAttribute("aria-pressed", activeStatusTab === "open" ? "true" : "false");
  if (tabHandled) tabHandled.setAttribute("aria-pressed", activeStatusTab === "handled" ? "true" : "false");
  if (openCountEl) openCountEl.textContent = openCount;
  if (handledCountEl) handledCountEl.textContent = handledCount;

  setNavBadge("feedback", feedbackList.filter((f) => !isHandled(f) && !isSolvedByHelp(f)).length + ratingsList.filter((f) => !isHandled(f) && noteOf(f)).length);

  // Render Table Rows
  if (filtered.length === 0) {
    const filtering = activeSearchTerm || (ratings ? ratingsWithNoteOnly : activeTopicFilter !== "ALL");
    const noun = ratings ? "ratings" : "feedback";
    const title = filtering ? `No ${noun} match` : (activeStatusTab === "handled" ? "Nothing handled yet" : `No open ${noun}`);
    const hint = filtering
      ? (ratings ? "Change the search or the note toggle." : "Change the search or the topic.")
      : (activeStatusTab === "handled" ? `${ratings ? "Ratings" : "Feedback"} you mark handled move${ratings ? "" : "s"} here.` : "Everything has been handled.");
    tbody.innerHTML = `
      <tr><td colspan="6" class="px-6 py-12 text-center">
        <p class="text-sm font-semibold text-gray-900 dark:text-white">${title}</p>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">${hint}</p>
      </td></tr>
    `;
    return;
  }

  tbody.innerHTML = filtered.map((item) => ratings ? ratingRowHtml(item) : feedbackRowHtml(item)).join("");

  // Attach Row Click Handlers
  tbody.querySelectorAll(".feedback-row").forEach((row) => {
    const checkbox = row.querySelector("[data-feedback-select]");
    if (checkbox) {
      checkbox.addEventListener("click", (e) => e.stopPropagation());
      checkbox.addEventListener("change", (e) => {
        const id = row.getAttribute("data-feedback-id");
        if (e.target.checked) selectedFeedbackIds.add(id);
        else selectedFeedbackIds.delete(id);
        updateSelectionControls(filtered);
      });
    }
    // The user id link goes to User Diagnostics, the recipe link to the recipe; the row around
    // them opens the drawer.
    row.querySelectorAll("[data-user-link], [data-recipe-link]").forEach((link) => link.addEventListener("click", (e) => e.stopPropagation()));
    row.addEventListener("click", () => {
      const id = row.getAttribute("data-feedback-id");
      const item = list.find((f) => f.id === id);
      if (item) openFeedbackModal(item);
    });
  });
}

// The controls that belong to one kind: the kind tabs, the topic filter (app feedback), the note
// toggle (ratings), the table's header row, the search box's hint and the card's text.
function updateKindControls() {
  const ratings = activeKind === "ratings";
  const kindApp = document.getElementById("feedbackKindApp");
  const kindRatings = document.getElementById("feedbackKindRatings");
  const kindAppCount = document.getElementById("feedbackKindAppCount");
  const kindRatingsCount = document.getElementById("feedbackKindRatingsCount");
  if (kindApp) kindApp.setAttribute("aria-pressed", ratings ? "false" : "true");
  if (kindRatings) kindRatings.setAttribute("aria-pressed", ratings ? "true" : "false");
  if (kindAppCount) kindAppCount.textContent = feedbackList.length;
  if (kindRatingsCount) kindRatingsCount.textContent = ratingsList.length;

  const topicWrap = document.getElementById("feedbackTopicFilterWrap");
  const noteWrap = document.getElementById("feedbackWithNoteOnlyWrap");
  if (topicWrap) topicWrap.style.display = ratings ? "none" : "grid";
  if (noteWrap) noteWrap.style.display = ratings ? "inline-flex" : "none";

  const headApp = document.getElementById("feedbackHeadApp");
  const headRatings = document.getElementById("feedbackHeadRatings");
  if (headApp) headApp.style.display = ratings ? "none" : "";
  if (headRatings) headRatings.style.display = ratings ? "" : "none";

  const search = document.getElementById("feedbackSearchInput");
  if (search) search.setAttribute("placeholder", ratings ? "Search note, recipe, tag, version" : "Search note, topic, user, email");
  const statTotalLabel = document.getElementById("statFeedbackTotalLabel");
  const statWithEmailLabel = document.getElementById("statFeedbackWithEmailLabel");
  if (statTotalLabel) statTotalLabel.textContent = ratings ? "Ratings loaded" : "Submissions";
  if (statWithEmailLabel) statWithEmailLabel.textContent = ratings ? "With a chef's note" : "With a follow-up email";
  const title = document.getElementById("feedbackTableTitle");
  const description = document.getElementById("feedbackTableDescription");
  if (title) title.textContent = ratings ? "Recipe ratings" : "Feedback from the apps";
  if (description) {
    description.textContent = ratings
      ? "The newest 100 from the Rate Recipe sheet, anonymous. Click a row to open it; the title opens the recipe."
      : "From the rating and improvement flows. Click a row to open it; a user id opens their diagnostics.";
  }
}

function ratingRowHtml(item) {
  const formattedDate = formatTimestamp(item.created_at);
  const tagsHtml = (item.quick_tags || []).map((tag) => `<span class="${BADGE.paprika}">${escapeHtml(tagLabel(tag))}</span>`).join("");
  const note = noteOf(item);
  const noteText = note ? escapeHtml(note) : `<em class="text-gray-400 dark:text-gray-500">No note written.</em>`;

  return `
      <tr class="feedback-row cursor-pointer align-top hover:bg-gray-50 has-checked:bg-paprika-50/60 dark:hover:bg-white/5 dark:has-checked:bg-white/5" data-feedback-id="${escapeHtml(item.id)}">
        <td class="py-4 pr-3 pl-4 sm:pl-6">${checkbox(`data-feedback-select aria-label="Select this rating" ${selectedFeedbackIds.has(item.id) ? "checked" : ""}`)}</td>
        <td class="px-3 py-4 text-sm whitespace-nowrap">
          <div class="font-medium text-gray-900 dark:text-white">${formattedDate.date}</div>
          <div class="text-xs text-gray-500 dark:text-gray-400">${formattedDate.time}</div>
        </td>
        <td class="px-3 py-4">
          <a href="${escapeHtml(recipeViewHref(item.recipe_id))}" data-recipe-link title="Open this recipe" class="${BUTTON.link} text-sm">${escapeHtml(recipeTitleOf(item))}</a>
          <div class="mt-0.5 font-mono text-xs break-all text-gray-500 dark:text-gray-400">${escapeHtml(item.recipe_id)}</div>
        </td>
        <td class="px-3 py-4">
          <div class="text-sm font-semibold whitespace-nowrap text-gray-900 dark:text-white">${scoreHtml(item.score)}</div>
          ${tagsHtml ? `<div class="mt-1 flex flex-wrap gap-1">${tagsHtml}</div>` : ""}
        </td>
        <td class="px-3 py-4 text-sm/6 break-words whitespace-pre-wrap text-gray-900 dark:text-gray-100">${noteText}</td>
        <td class="py-4 pr-4 pl-3 text-xs text-gray-500 sm:pr-6 dark:text-gray-400">${escapeHtml(ratingDeviceText(item))}${item.country_code ? `<br>${escapeHtml(item.country_code)}` : ""}</td>
      </tr>
    `;
}

function feedbackRowHtml(item) {
    const formattedDate = formatTimestamp(item.created_at);
    const topicsHtml = (item.topics || []).map((topic) => `<span class="${BADGE.paprika}">${escapeHtml(topic)}</span>`).join("")
      || `<span class="text-sm text-gray-400 dark:text-gray-500">General</span>`;
    const noteText = item.note && item.note.trim()
      ? escapeHtml(item.note)
      : `<em class="text-gray-400 dark:text-gray-500">No message written.</em>`;
    const contactEmailHtml = item.contact_email
      ? `<div class="mb-1 flex items-center gap-1 text-sm font-medium break-all text-sky-700 dark:text-sky-400">${solidIcon("envelope", "size-4 flex-none")}${escapeHtml(item.contact_email)}</div>`
      : "";

    // Muted when the help the app offered solved it (#2647): the feedback stays, it needs nobody.
    const muted = isSolvedByHelp(item) ? " opacity-60" : "";

    return `
      <tr class="feedback-row cursor-pointer align-top hover:bg-gray-50 has-checked:bg-paprika-50/60 dark:hover:bg-white/5 dark:has-checked:bg-white/5${muted}" data-feedback-id="${escapeHtml(item.id)}">
        <td class="py-4 pr-3 pl-4 sm:pl-6">${checkbox(`data-feedback-select aria-label="Select this feedback" ${selectedFeedbackIds.has(item.id) ? "checked" : ""}`)}</td>
        <td class="px-3 py-4 text-sm whitespace-nowrap">
          <div class="font-medium text-gray-900 dark:text-white">${formattedDate.date}</div>
          <div class="text-xs text-gray-500 dark:text-gray-400">${formattedDate.time}</div>
        </td>
        <td class="px-3 py-4">
          <div>${userLinkHtml(item.user_id, "font-semibold")}</div>
          ${item.family_id ? `<div class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Family <span class="font-mono">${escapeHtml(item.family_id)}</span></div>` : ""}
        </td>
        <td class="px-3 py-4"><div class="flex flex-wrap gap-1">${topicsHtml}</div></td>
        <td class="px-3 py-4 text-sm/6 break-words text-gray-900 dark:text-gray-100"><div class="whitespace-pre-wrap">${noteText}</div>${helpOutcomeHtml(item)}</td>
        <td class="py-4 pr-4 pl-3 sm:pr-6">
          ${contactEmailHtml}
          <div class="text-xs text-gray-500 dark:text-gray-400">${escapeHtml(item.app_version || "1.0.0")} (${escapeHtml(item.build_number || "1")})<br>${escapeHtml(item.device_model || "iPhone")} · iOS ${escapeHtml(item.ios_version || "")}</div>
        </td>
      </tr>
    `;
}

/**
 * Opens the Feedback Detail Modal
 */
export function openFeedbackModal(item) {
  selectedFeedbackItem = item;
  const modal = document.getElementById("feedbackDetailModal");
  if (!modal) return;

  const formattedDate = formatTimestamp(item.created_at);
  const tsEl = document.getElementById("modalFeedbackTimestamp");
  const topicsEl = document.getElementById("modalFeedbackTopics");
  const noteEl = document.getElementById("modalFeedbackNote");
  const emailEl = document.getElementById("modalFeedbackEmail");
  const deviceEl = document.getElementById("modalFeedbackDevice");
  const userEl = document.getElementById("modalFeedbackUserId");
  const famEl = document.getElementById("modalFeedbackFamilyId");
  const jsonEl = document.getElementById("modalFeedbackRawJson");
  const statusEl = document.getElementById("modalFeedbackStatus");
  const handledBtn = document.getElementById("modalToggleHandledBtn");
  const rating = isRecipeRating(item);
  const titleEl = document.getElementById("feedbackDrawerTitle");
  const topicsLabel = document.getElementById("modalFeedbackTopicsLabel");
  const noteLabel = document.getElementById("modalFeedbackNoteLabel");
  const emailLabel = document.getElementById("modalFeedbackEmailLabel");
  const userRow = document.getElementById("modalFeedbackUserRow");
  const famRow = document.getElementById("modalFeedbackFamilyRow");
  const helpRow = document.getElementById("modalFeedbackHelpRow");
  const helpEl = document.getElementById("modalFeedbackHelp");

  if (titleEl) titleEl.textContent = rating ? "Recipe rating" : "Feedback";
  if (topicsLabel) topicsLabel.textContent = rating ? "Score and quick tags" : "Topics";
  if (noteLabel) noteLabel.textContent = rating ? "Chef's note" : "Message";
  if (emailLabel) emailLabel.textContent = rating ? "Recipe" : "Contact email";
  if (userRow) userRow.style.display = rating ? "none" : "flex";
  if (famRow) famRow.style.display = rating ? "none" : "flex";
  const helpText = rating ? "" : helpOutcomeText(helpOutcomeOf(item));
  if (helpRow) helpRow.style.display = helpText ? "flex" : "none";
  if (helpEl) helpEl.textContent = helpText;

  if (tsEl) tsEl.textContent = `${formattedDate.date} at ${formattedDate.time}`;

  if (topicsEl) {
    if (rating) {
      const tags = (item.quick_tags || []).map((t) => `<span class="${BADGE.paprika}">${escapeHtml(tagLabel(t))}</span>`).join("");
      // The score, its meaning as the apps word it above the slider, then the quick tags.
      topicsEl.innerHTML = `<span class="${BADGE.gray} text-sm" title="${escapeHtml(foodScoreReaction(item.score))}">${escapeHtml(formatScore(item.score))} / 10</span>`
        + (foodScoreReaction(item.score) ? `<span class="self-center text-sm text-gray-700 dark:text-gray-300">${escapeHtml(foodScoreReaction(item.score))}</span>` : "")
        + tags;
    } else {
      topicsEl.innerHTML = (item.topics || []).map((t) => `<span class="${BADGE.paprika}">${escapeHtml(t)}</span>`).join("")
        || `<span class="text-sm text-gray-400 dark:text-gray-500">None chosen</span>`;
    }
  }

  if (noteEl) {
    const note = noteOf(item);
    noteEl.textContent = note || (rating ? "No note written." : "No written message provided.");
  }

  if (emailEl) {
    if (rating) {
      emailEl.innerHTML = `<a href="${escapeHtml(recipeViewHref(item.recipe_id))}" data-recipe-link class="${BUTTON.link}">${escapeHtml(recipeTitleOf(item))}</a><div class="mt-0.5 font-mono text-xs font-normal break-all text-gray-500 dark:text-gray-400">${escapeHtml(item.recipe_id)}</div>`;
      // Leaving for the recipe closes the drawer behind it.
      emailEl.querySelectorAll("[data-recipe-link]").forEach((link) => link.addEventListener("click", () => closeFeedbackModal()));
    } else if (item.contact_email) {
      emailEl.innerHTML = `<a href="mailto:${escapeHtml(item.contact_email)}" class="text-sky-700 underline hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300">${escapeHtml(item.contact_email)}</a>`;
    } else {
      emailEl.innerHTML = `<span class="font-normal text-gray-400 dark:text-gray-500">Not given</span>`;
    }
  }

  if (deviceEl) {
    deviceEl.textContent = rating
      ? [ratingDeviceText(item), item.locale || item.country_code || ""].filter(Boolean).join(" · ")
      : `${item.device_model || "iPhone"} · iOS ${item.ios_version || "Unknown"} (App v${item.app_version || "1.0.0"} build ${item.build_number || "1"})`;
  }

  if (userEl) {
    userEl.innerHTML = rating ? "" : userLinkHtml(item.user_id, "font-semibold");
    // Leaving for the user's diagnostics closes the drawer behind it.
    userEl.querySelectorAll("[data-user-link]").forEach((link) => link.addEventListener("click", () => closeFeedbackModal()));
  }

  if (statusEl) {
    if (isHandled(item)) {
      const when = formatTimestamp(item.handled_at);
      const detail = [when.date !== "Unknown" ? `${when.date} at ${when.time}` : "", item.handled_by ? `by ${item.handled_by}` : ""].filter(Boolean).join(" ");
      statusEl.innerHTML = `<span class="${BADGE.green}">Handled</span>${detail ? `<span class="ml-2 text-xs text-gray-500 dark:text-gray-400">${escapeHtml(detail)}</span>` : ""}`;
    } else {
      statusEl.innerHTML = `<span class="${BADGE.yellow}">Open</span>`;
    }
  }

  if (handledBtn) {
    handledBtn.textContent = isHandled(item) ? "Reopen" : "Mark as handled";
  }

  if (famEl) {
    famEl.textContent = item.family_id || "None";
  }

  if (jsonEl) {
    jsonEl.textContent = JSON.stringify(item, null, 2);
  }

  showDialog("feedbackDetailModal");
}

/**
 * Closes the Feedback Detail Modal
 */
export function closeFeedbackModal() {
  hideDialog("feedbackDetailModal");
  selectedFeedbackItem = null;
}

function formatTimestamp(ts) {
  if (!ts) return { date: "Unknown", time: "" };
  let d;
  if (ts.toDate) d = ts.toDate();
  else if (ts.toMillis) d = new Date(ts.toMillis());
  else d = new Date(ts);

  if (isNaN(d.getTime())) return { date: "Unknown", time: "" };

  const dateStr = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const timeStr = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  return { date: dateStr, time: timeStr };
}

function escapeHtml(str) {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
